import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { BrandProvider, useBrand } from './BrandContext';
import ProtectedRoute from './ProtectedRoute';
import Home from './Home';
import Admin from './Admin';
import Kursplan from './Kursplan';
import EventsAndMore from './EventsAndMore';
import Geburtstage from './Geburtstage';
import FeriencampsBooking from './FeriencampsBooking';
import Schnuppern from './Schnuppern';
import Schnitzeljagd from './Schnitzeljagd';
import Internetfuehrerschein from './Internetfuehrerschein';
import Impressum from './Impressum';
import Datenschutz from './Datenschutz';
import Agb from './Agb';
import MitgliedschaftAntrag from './MitgliedschaftAntrag';
import Login from './Login';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import AdminForgotPassword from './AdminForgotPassword';
import AdminResetPassword from './AdminResetPassword';
import AdminForceChangePassword from './AdminForceChangePassword';
import WorkshopCancellation from './WorkshopCancellation';
import KursinhaltZugang from './KursinhaltZugang';
import MemberDashboard from './MemberDashboard';
import KiWorkshop from './KiWorkshop';
import ProgrammierenWorkshop from './ProgrammierenWorkshop';
import SocialMediaProsWorkshop from './SocialMediaProsWorkshop';
import NewsletterSubscribe from './NewsletterSubscribe';
import NewsletterConfirm from './NewsletterConfirm';
import NewsletterUnsubscribe from './NewsletterUnsubscribe';
import './App.css';

function Navigation() {
  const { user, logout, isAdmin, isMember } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const [backendStatus, setBackendStatus] = useState('checking');

  useEffect(() => {
    fetch('/api/courses')
      .then(response => {
        if (response.ok) {
          setBackendStatus('online');
        } else {
          setBackendStatus('offline');
        }
      })
      .catch(() => {
        setBackendStatus('offline');
      });
  }, []);

  const isActive = (path) => location.pathname === path ? 'nav-link active' : 'nav-link';

  return (
    <header className="app-header mb-4">
      <div className="d-flex justify-content-between align-items-center py-3">
        <Link to="/" className="d-flex align-items-center text-decoration-none gap-3">
          <img
            src="/logo.png"
            alt={brand.fullName + ' Logo'}
            className="brand-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="brand-name">
            {brand.nameBase}<span>{brand.nameSuffix}</span>
          </span>
        </Link>
        <div className="d-flex align-items-center gap-3">
          {user ? (
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-info">{user.userType === 'admin' ? 'Admin' : user.name || user.username}</span>
              <button className="btn btn-outline-secondary btn-sm" onClick={logout}>Abmelden</button>
            </div>
          ) : (
            <Link className="btn btn-primary btn-sm" to="/login">Anmelden</Link>
          )}
          <div className="d-flex align-items-center">
            {backendStatus === 'online' && <span className="badge bg-success">Online</span>}
            {backendStatus === 'offline' && <span className="badge bg-danger">Offline</span>}
            {backendStatus === 'checking' && <span className="badge bg-warning">...</span>}
          </div>
        </div>
      </div>
      <nav className="nav py-2">
        <Link className={isActive('/')} to="/">Home</Link>
        {brand.routes.kursplan && <Link className={isActive('/kursplan')} to="/kursplan">Kursplan</Link>}
        {brand.routes.events && <Link className={isActive('/events')} to="/events">Workshops & Events</Link>}
        {brand.routes.geburtstage && <Link className={isActive('/geburtstage')} to="/geburtstage">Geburtstage</Link>}
        {brand.routes.feriencamps && <Link className={isActive('/feriencamps')} to="/feriencamps">Feriencamps</Link>}
        {brand.routes.schnuppern && <Link className={isActive('/schnuppern')} to="/schnuppern">Schnuppern</Link>}
        {brand.routes.coaching && <Link className={isActive('/coaching')} to="/coaching">Coaching</Link>}
        {isAdmin && <Link className={isActive('/admin')} to="/admin">Admin</Link>}
        {isMember && <Link className={isActive('/member')} to="/member">Mein Bereich</Link>}
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="app-footer mt-5 py-4 border-top">
      <div className="d-flex justify-content-center gap-4">
        <Link to="/impressum" className="text-muted text-decoration-none">Impressum</Link>
        <Link to="/datenschutz" className="text-muted text-decoration-none">Datenschutz</Link>
      </div>
      <div className="text-center text-muted mt-2">
        <small>&copy; {new Date().getFullYear()} Futurhinos & Futurinas. Alle Rechte vorbehalten.</small>
      </div>
    </footer>
  );
}

function AppContent() {
  const brand = useBrand();
  return (
    <div className="container mt-4 d-flex flex-column min-vh-100">
      <Navigation />
      <main className="flex-grow-1">
        <Routes>
          {/* Geteilte Routes (beide Tenants) */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/*" element={
            <ProtectedRoute requiredUserType="admin">
              <Admin />
            </ProtectedRoute>
          } />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/agb" element={<Agb />} />
          <Route path="/passwort-vergessen" element={<ForgotPassword />} />
          <Route path="/passwort-zuruecksetzen/:token" element={<ResetPassword />} />
          <Route path="/admin-passwort-vergessen" element={<AdminForgotPassword />} />
          <Route path="/admin-passwort-zuruecksetzen/:token" element={<AdminResetPassword />} />
          <Route path="/admin/passwort-aendern" element={<AdminForceChangePassword />} />
          <Route path="/newsletter/subscribe" element={<NewsletterSubscribe />} />
          <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />
          <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
          <Route path="/stornierung/workshop/:token" element={<WorkshopCancellation />} />
          <Route path="/kursinhalt/:courseId" element={<KursinhaltZugang />} />
          <Route path="/member" element={
            <ProtectedRoute requiredUserType="member">
              <MemberDashboard />
            </ProtectedRoute>
          } />

          {/* Tenant-spezifische Routes */}
          {brand.routes.events && <Route path="/events" element={<EventsAndMore />} />}
          {brand.routes.kursplan && <Route path="/kursplan" element={<Kursplan />} />}
          {brand.routes.schnuppern && <Route path="/schnuppern" element={<Schnuppern />} />}
          {brand.routes.geburtstage && <Route path="/geburtstage" element={<Geburtstage />} />}
          {brand.routes.feriencamps && <Route path="/feriencamps" element={<FeriencampsBooking />} />}

          {/* Kids-spezifische Workshop-Landingpages (haengen am Kids-Tenant) */}
          {brand.id === 'kids' && <Route path="/schnitzeljagd" element={<Schnitzeljagd />} />}
          {brand.id === 'kids' && <Route path="/internetfuehrerschein" element={<Internetfuehrerschein />} />}
          {brand.id === 'kids' && <Route path="/ki-workshop" element={<KiWorkshop />} />}
          {brand.id === 'kids' && <Route path="/programmieren-workshop" element={<ProgrammierenWorkshop />} />}
          {brand.id === 'kids' && <Route path="/pfingstcamp" element={<SocialMediaProsWorkshop />} />}
          {brand.id === 'kids' && <Route path="/mitgliedschaft" element={<MitgliedschaftAntrag />} />}
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <BrandProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrandProvider>
    </Router>
  );
}

export default App;
